import { useEffect, useState } from "react"
import config from "config/app.config"

// Module-level cache so all tabs share one fetch per page load.
let cachePromise = null

const emptyMap = {
  accessories: new Set(),
  special: new Set(),
  cladding: new Set(),
  pagkos: new Set(),
  box: new Set(),
}

async function loadStoreTabMap() {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}")
  const headers = {
    Accept: "application/json",
    "X-Api-Key": config.APP_API_KEY,
    Authorization: authUser.session_token ? `Bearer ${authUser.session_token}` : "",
  }

  // 1. Resolve StoreTab category → its 5 option IDs
  const catResp = await fetch(
    `${config.APP_URL}_table/product_categories?filter=${encodeURIComponent("(name=StoreTab)")}&limit=1`,
    { headers },
  )
  const catData = await catResp.json()
  const storeTabCat = (catData.resource || [])[0]
  if (!storeTabCat) return { ...emptyMap, loaded: true }

  const optResp = await fetch(
    `${config.APP_URL}_table/product_category_options?filter=${encodeURIComponent(`(category_id=${storeTabCat.id})`)}&limit=50`,
    { headers },
  )
  const optData = await optResp.json()
  const options = optData.resource || []
  const optIdByName = {}
  for (const o of options) optIdByName[o.option_name] = o.id

  const wantedIds = Object.values(optIdByName)
  if (wantedIds.length === 0) return { ...emptyMap, loaded: true }

  // 2. Fetch all mappings for those options in one call
  const mapResp = await fetch(
    `${config.APP_URL}_table/product_categories_mapping?filter=${encodeURIComponent(
      `(category_option_id in (${wantedIds.join(",")}))`,
    )}&limit=50000`,
    { headers },
  )
  const mapData = await mapResp.json()
  const mappings = mapData.resource || []

  const result = {
    accessories: new Set(),
    special: new Set(),
    cladding: new Set(),
    pagkos: new Set(),
    box: new Set(),
    loaded: true,
  }
  const nameByOptId = {}
  for (const [name, id] of Object.entries(optIdByName)) nameByOptId[id] = name

  for (const m of mappings) {
    const name = nameByOptId[m.category_option_id]
    if (!name) continue
    const bucket = result[name.toLowerCase()]
    if (bucket) bucket.add(m.product_id)
  }
  return result
}

// Shared hook — returns the current storeTab map (or empty until loaded).
// Callers should treat missing IDs as "no M2M tag" and fall back to legacy JSON.
export default function useStoreTabCategoryMap() {
  const [state, setState] = useState({ ...emptyMap, loaded: false })

  useEffect(() => {
    if (!cachePromise) cachePromise = loadStoreTabMap().catch(err => {
      console.error("[useStoreTabCategoryMap] Failed to load:", err)
      return { ...emptyMap, loaded: true }
    })
    let alive = true
    cachePromise.then(map => { if (alive) setState(map) })
    return () => { alive = false }
  }, [])

  return state
}

// Escape hatch for tests — force a refresh next mount.
export function _resetStoreTabCache() { cachePromise = null }
